import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Users, Plus, Pencil, Trash2, Shield, ShieldCheck, ShieldAlert,
  Eye, EyeOff, ArrowLeft, LogOut, UserPlus, Check, X, Key
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(null);
  const { authenticated, currentUser, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    roles: ['viewer'],  // Changed to array for multiple roles
    is_active: true
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  // Check if user is super_admin and fetch data
  useEffect(() => {
    if (authenticated && currentUser) {
      if (currentUser.role !== 'super_admin') {
        toast.error('Only super admin can manage users');
        navigate('/admin/dashboard');
        return;
      }
      fetchUsers();
      fetchRoles();
    }
  }, [authenticated, currentUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/roles`, {
        headers: getAuthHeader()
      });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      roles: ['viewer'],
      is_active: true
    });
    setShowAddForm(false);
    setEditingUser(null);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name || '',
      password: '',
      roles: user.roles || [user.role || 'viewer'],
      is_active: user.is_active
    });
    setShowAddForm(true);
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(role)) {
        // Remove role (but keep at least one)
        const newRoles = currentRoles.filter(r => r !== role);
        return { ...prev, roles: newRoles.length > 0 ? newRoles : ['viewer'] };
      } else {
        // Add role
        return { ...prev, roles: [...currentRoles, role] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.email) {
      toast.error('Username and email are required');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    if (!formData.roles || formData.roles.length === 0) {
      toast.error('At least one role must be selected');
      return;
    }

    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        await axios.put(`${API}/admin/users/${editingUser._id}`, updateData, {
          headers: getAuthHeader()
        });
        toast.success('User updated successfully');
      } else {
        // Create user
        await axios.post(`${API}/admin/users`, formData, {
          headers: getAuthHeader()
        });
        toast.success('User created successfully');
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user._id}`, {
        is_active: !user.is_active
      }, {
        headers: getAuthHeader()
      });
      toast.success(`User ${user.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handlePasswordChange = async (userId) => {
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        password: passwordData.password
      }, {
        headers: getAuthHeader()
      });
      toast.success('Password updated successfully');
      setShowPasswordChange(null);
      setPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to change password');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <ShieldAlert className="h-4 w-4 text-red-600" />;
      case 'admin':
        return <ShieldCheck className="h-4 w-4 text-orange-600" />;
      case 'manager':
        return <Shield className="h-4 w-4 text-blue-600" />;
      case 'editor':
        return <Shield className="h-4 w-4 text-green-600" />;
      default:
        return <Shield className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'admin':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'manager':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'editor':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">User Management</h1>
            <p className="text-sm text-gray-500">Add, edit, and manage user accounts and roles</p>
          </div>
          <Button onClick={() => { resetForm(); setShowAddForm(true); }}>
            <UserPlus className="h-4 w-4 mr-2" /> Add User
          </Button>
        </div>

        {/* Add/Edit User Form */}
        {showAddForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {editingUser ? <Pencil className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
                {editingUser ? 'Edit User' : 'Add New User'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                  <Input
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingUser ? 'New Password (leave empty to keep current)' : 'Password *'}
                  </label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roles * (select multiple)</label>
                  <div className="flex flex-wrap gap-2">
                    {roles.map(role => {
                      const isSelected = (formData.roles || []).includes(role.name);
                      return (
                        <button
                          key={role.name}
                          type="button"
                          onClick={() => handleRoleToggle(role.name)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            isSelected 
                              ? `${getRoleBadgeClass(role.name)} border-current font-semibold ring-2 ring-offset-1` 
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {isSelected && <Check className="h-4 w-4" />}
                          {getRoleIcon(role.name)}
                          <span className="capitalize">{role.name.replace('_', ' ')}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    User will have combined permissions from all selected roles
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Active Account</span>
                  </label>
                </div>

                <div className="md:col-span-2 flex gap-3">
                  <Button type="submit">
                    {editingUser ? 'Update User' : 'Create User'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Role Legend */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3">Role Permissions</h3>
            <div className="flex flex-wrap gap-4">
              {roles.map(role => (
                <div key={role.name} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getRoleBadgeClass(role.name)}`}>
                  {getRoleIcon(role.name)}
                  <span className="font-medium capitalize">{role.name.replace('_', ' ')}</span>
                  <span className="text-xs opacity-75">({role.description})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              All Users ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-gray-600">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                No users found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-4 font-semibold">User</th>
                      <th className="text-left p-4 font-semibold">Roles</th>
                      <th className="text-center p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Last Login</th>
                      <th className="text-center p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const userRoles = user.roles || [user.role || 'viewer'];
                      return (
                      <tr key={user._id} className={`border-b hover:bg-gray-50 ${!user.is_active ? 'opacity-50' : ''}`}>
                        <td className="p-4">
                          <div>
                            <p className="font-semibold text-gray-900">{user.full_name || user.username}</p>
                            <p className="text-sm text-gray-500">@{user.username}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {userRoles.map((role, idx) => (
                              <span key={idx} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeClass(role)}`}>
                                {getRoleIcon(role)}
                                {role.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {user.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          {user.last_login 
                            ? new Date(user.last_login).toLocaleString()
                            : 'Never'
                          }
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            {/* Password Change */}
                            {showPasswordChange === user._id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="password"
                                  placeholder="New password"
                                  value={passwordData.password}
                                  onChange={(e) => setPasswordData({...passwordData, password: e.target.value})}
                                  className="w-32 h-8 text-sm"
                                />
                                <Input
                                  type="password"
                                  placeholder="Confirm"
                                  value={passwordData.confirmPassword}
                                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                  className="w-32 h-8 text-sm"
                                />
                                <button
                                  onClick={() => handlePasswordChange(user._id)}
                                  className="p-1.5 rounded bg-green-100 text-green-600 hover:bg-green-200"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => { setShowPasswordChange(null); setPasswordData({ password: '', confirmPassword: '' }); }}
                                  className="p-1.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => setShowPasswordChange(user._id)}
                                  className="p-2 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                                  title="Change Password"
                                >
                                  <Key className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleToggleActive(user)}
                                  className={`p-2 rounded-full transition-colors ${
                                    user.is_active
                                      ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                  title={user.is_active ? 'Disable User' : 'Enable User'}
                                >
                                  {user.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                                <button
                                  onClick={() => handleEdit(user)}
                                  className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                                  title="Edit User"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                {user.role !== 'super_admin' && currentUser?.id !== user._id && (
                                  <button
                                    onClick={() => handleDelete(user._id, user.username)}
                                    className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                    title="Delete User"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
};

export default AdminUsers;
